import React, { useRef, useState, FC, useMemo } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';
import { connect, useSelector } from 'react-redux';

import { scale, width } from '@components/frame';
import { isPhone, isWeb, navigateTo, localTime, capitalizeFirstLetter, controlAPICall, formatStartOf, navigationRef } from '@helpers';
import Line from '@components/common/line';

import { getBoxShadowStyle } from '@styles/shadow';
import { font } from '@styles/fonts';

import Chart from './components/chart';
import Arrow from '@components/common/arrow';
import NcFileItem from './components/ncFileItem';
import Item from '@components/machines/components/item';

import Selector from '@styles/selectors';
import { SmallLoading } from '@components/loading';
import FeatureFlags from '@components/featureFlags';
import ColorWrapper from '@styles/colors';
import NCFileItemForDetailsScreen from '@components/ncFiles/components/eventsChart/components/ncFileItem';
import { getPeriodStep, getStatsRequestParams } from './helpers/period';

import {
  getMachineStatsPerPeriod,
  getMachineEvent,
  getMachineStatistics,
  getNCFileStatistics,
  setSelectedMachine,
  setSelectedNCFile,
  getMachineEventTimeline,
  getMachineStatisticsForNCFile,
  getNcFileStatsMonth,
  getNcFileStatsDay,
  getNcFileStatsHour,
} from '../../../../reducers/machines';
import { generateImageURL } from '@reducers/blobStorage';
import {
  machineEventDetailsWallboard,
  getNCFileById,
  getNCFileByName,
  setSelectedNCFile as setSelectedNCFileManufacture,
} from '../../../../reducers/manufacture';

import { sendLog } from '@reducers/dataDogs';
import { ncFilesSearch } from '@reducers/search';
import { EventChartsProps, EventChartWrapperProps, MobileMachinesListProps } from '@v2/pages/machines/common/types/event';
import { InlineErrorBoundary } from '@v2/components/errorBoundary';
import { useFocusEffect, useRoute } from '@react-navigation/native';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import { MachinesScreen, TimePeriod } from '@v2/pages/machines/common/enums';
import {
  DATE_FORMAT,
  PERFORMANCE_CHART_BLOCK_WIDTH,
  SCREEN_BORDER_RADIUS,
  SCREEN_MARGIN_BOTTOM_PHONE,
  SCREEN_MARGIN_BOTTOM_WEB,
  SCREEN_PADDING_HORIZONTAL,
  SCREEN_PADDING_VERTICAL_FOR_ITEM,
  TEST_IDS,
} from '../../../v2/pages/common/constants/base.constants';
import { setChartData, setInitialEventDate, setSelectedChartDate, setSelectedChartDateEnd } from '@reducers/machinesV2';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { setTimePeriod } from '@reducers/params';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';
import { selectNcFileId } from '@reducers/details';
import { getChartPropsByScope } from './helpers/getChartPropsByScope';
import { useMobileChartReset } from './hooks/useMobileChartReset';
import { KEYS_TAGS } from '@components/imageHandling/common';
import { metricTypes } from '@v2/pages/machines/common/constants';

let isFirstLoad = true;

const MobileMachinesListComponent: FC<MobileMachinesListProps> = ({
  machines,
  machineStatistics,
  machinesLoaded,
  selectedItem,
  setSelectedMachine,
  time,
  generateImageURL,
  C,
  selectedGroup,
}) => {
  const {
    server: { offset = 0 },
  } = time;

  const { isNowSubScreen } = useHandleNavigationMachinesHeader();
  const { isAllSelected } = useHandleParams();

  const displayTime = isNowSubScreen;
  const listWidth = scale(360);

  const renderArray = displayTime ? machines : machineStatistics.data;
  const showLoading = displayTime ? false : !machineStatistics.loaded;

  const selectedGroupIDs = selectedGroup?.machines?.map((i) => Number(i.machineId)) || [];

  const renderArrayFilteredByGroup = useMemo(() => {
    return renderArray.filter((i) => (selectedGroup ? selectedGroupIDs.includes(Number(i.id)) : true));
  }, [renderArray, selectedGroup, selectedGroupIDs]);

  const additionalData = displayTime
    ? {}
    : {
        maxValueActive: machineStatistics.maxValueActive,
        maxValueProductive: machineStatistics.maxValueProductive,
      };

  const loading = showLoading || !machinesLoaded;

  const shadowStyles = getBoxShadowStyle({ type: 'Field', C });

  return (
    <View style={[!loading && isWeb && { borderRadius: scale(SCREEN_BORDER_RADIUS) }, !loading && isWeb && shadowStyles, { marginTop: scale(28) }]}>
      {showLoading || !machinesLoaded ? (
        <View style={{ width: '100%', marginTop: scale(50) }}>
          <SmallLoading />
        </View>
      ) : (
        renderArrayFilteredByGroup?.map((item, index) => (
          <View style={{ position: 'relative' }} key={`listitem-${item.machineId}`}>
            <Item
              {...{
                generateImageURL,
                item: {
                  machineStatus: {},
                  ...item,
                  ...additionalData,
                },
                setSelectedMachine,
                timeOffset: offset,
                nextSelected: renderArrayFilteredByGroup[index + 1]
                  ? renderArrayFilteredByGroup[index + 1]?.id === selectedItem?.id && !isAllSelected
                  : false,
                selected: !isAllSelected && selectedItem?.id === item.id,
                loading: !machinesLoaded,
                displayTime,
                listWidth,
                roundTop: index === 0,
                roundBottom: index === renderArrayFilteredByGroup.length - 1,
              }}
            />
          </View>
        ))
      )}
    </View>
  );
};

const MobileMachinesList = connect(
  ({ machinesV2: { selectedGroup }, machines: { machines, machineStatistics, machinesLoaded } }) => ({
    selectedGroup,
    machines,
    machineStatistics,
    machinesLoaded,
  }),
  {}
)(ColorWrapper(MobileMachinesListComponent));

export const EventCharts: FC<EventChartsProps> = ({
  C,
  getMachineStatistics,
  getNCFileStatistics,
  selectedChartDate,
  selectedChartDateEnd,
  setSelectedChartDate,

  setInitialEventDate,
  getMachineStatsPerPeriod,
  getMachineEvent,
  sendLog,
  chartData: data,
  setChartData: setData,
  generateImageURL,
  selectedItemFromMachines,
  ncFileStatistics,
  setSelectedMachine,
  time,
  setSelectedNCFile,
  abortControllerNCFile,
  selectedTimeCounter,
  changeDate,
  setTimePeriod,
  getMachineStatisticsForNCFile,
  machineStatisticsForNCFile,
  getNcFileStatsMonth,
  getNcFileStatsDay,
  getNcFileStatsHour,
  scope,
}) => {
  const abortControllerMachine = useRef();
  const route = useRoute();
  const { isAllSelected: allSelected, id, timePeriod: selectedTime, showLineChart: showLine } = useHandleParams();
  const { IS_NARROW_WEB } = useDeviceSize();

  const chartTitle = Object.values(metricTypes).find((title) => route?.name.includes(title));

  const { statisticsScope: statisticsScopeFromNavigation, isNowSubScreen } = useHandleNavigationMachinesHeader();
  const { safeIsMachinesScreenCheck } = useNavigationCheck();

  const statisticsScope = scope ?? statisticsScopeFromNavigation;
  const isMachineScope = statisticsScope === KEYS_TAGS.Machine;

  const shortName = selectedItemFromMachines?.shortName;

  const ncFileId = useSelector(selectNcFileId);

  const [ncFiles, setNcFiles] = useState([]);
  const [dataPrev, setDataPrev] = useState({});

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const { resetMobileChartCount, setSelectedChartDateWithReset: setSelectedChartDateFromCalendar } = useMobileChartReset(setSelectedChartDate);

  const isDay = selectedTime === TimePeriod.DAY;

  const mobileGlobalTabs = {
    perMachine: 'Per Machine',
    perNCFile: 'Per NC File',
  };

  const [selectedMobileGlobalTab, setSelectedMobileGlobalTab] = useState(mobileGlobalTabs.perMachine);

  const isActive = route.name.includes(MachinesScreen.ACTIVE);

  const navigationData = {
    initialTab: 'ncFiles',
    search: JSON.stringify({
      id,
      start: data?.start,
      end: data?.end,
      shortName,
      files: ncFiles.map((i) => i.NcFileMain).join(),
    }),
  };

  const updateType = [
    {
      name: 'Update NC File Type',
      alert: ncFiles.length,
      onClick: () => {
        navigateTo(
          'SearchScreen',
          isWeb
            ? navigationData
            : {
                screen: 'Search',
                params: navigationData,
              }
        );
      },
    },
  ];

  const fullWidth = isPhone && !IS_NARROW_WEB ? width - scale(SCREEN_PADDING_HORIZONTAL) * 2 : scale(PERFORMANCE_CHART_BLOCK_WIDTH);
  const fullHeight = isActive && !showLine ? scale(243) : scale(276);

  const toGo = (selected, configOnly = false) => {
    const selectedTime = data?.selectedTime as keyof typeof nextTime;
    const nextTime = {
      year: 'month',
      month: 'day',
      week: 'day',
    };

    if (configOnly) {
      return {
        section: nextTime[selectedTime],
        date: selected.dateISO,
      };
    }

    if (data?.selectedTime === 'day') {
      setInitialEventDate(localTime(selected.dateISO).add(1, 'h').format());
      // setShowEventsList(true);
      return;
    }
    setSelectedChartDate(selected.dateISO);
    setTimePeriod(nextTime[selectedTime]);
  };

  useFocusEffect(
    React.useCallback(() => {
      setTimePeriod(route?.params?.timePeriod || selectedTime);
    }, [route?.params?.timePeriod, selectedTime, setTimePeriod])
  );

  useFocusEffect(
    React.useCallback(() => {
      if (!safeIsMachinesScreenCheck) return; // this call is only needed for machines list which is visible only on machines screens
      setSelectedIndex(null);
      setSelectedItem(null);

      const momentStart = selectedChartDate ? localTime(selectedChartDate) : localTime();
      const d = getStatsRequestParams(selectedTime, momentStart);

      controlAPICall(getMachineStatistics, d, abortControllerMachine);

      isFirstLoad = false;
    }, [selectedTime, selectedChartDate, selectedTimeCounter, safeIsMachinesScreenCheck])
  );

  useFocusEffect(
    React.useCallback(() => {
      if (isMachineScope || !ncFileId) return;

      const momentStart = selectedChartDate ? localTime(selectedChartDate) : localTime();

      const d = { ...getStatsRequestParams(selectedTime, momentStart), ncFileId };

      controlAPICall(getMachineStatisticsForNCFile, d, abortControllerNCFile);
    }, [selectedTime, selectedChartDate, selectedTimeCounter, ncFileId, isMachineScope])
  );

  useFocusEffect(
    React.useCallback(() => {
      if (!isMachineScope) return;

      const momentStart = selectedChartDate ? localTime(selectedChartDate) : localTime();

      const d: { period: string; date: string; numberOfWeeks?: number; machineId?: number | string; reset: boolean } = {
        ...getStatsRequestParams(selectedTime, momentStart),
        machineId: id,
        reset: true,
      };

      if (allSelected) {
        delete d.machineId;
      }

      controlAPICall(getNCFileStatistics, d, abortControllerNCFile);
    }, [id, selectedTime, selectedChartDate, selectedTimeCounter, allSelected, isMachineScope])
  );

  const hasNextWindow = (() => {
    if (!data?.start || !data?.selectedTime) return false;
    const { unit: stepUnit, count: stepCount } = getPeriodStep(data.selectedTime);
    return !localTime(data.start).add(stepCount, stepUnit).isAfter(localTime());
  })();

  return (
    <View
      style={[
        isWeb && { flex: 1 },
        {
          paddingTop: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM),
        },
      ]}
    >
      <View
        style={[
          {
            width: isPhone && !IS_NARROW_WEB ? '100%' : scale(isWeb ? 632 : 532),
          },
        ]}
      >
        <View>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {isWeb && <Arrow {...{ C, onPress: () => changeDate('subtract'), testID: TEST_IDS.activeProductiveChartNavigation.leftArrow }} />}
            <InlineErrorBoundary screenName="EventChartsChartBlock">
              <Chart
                {...{
                C,
                setData,
                setDataPrev,
                setNcFiles,
                data,
                dataPrev,
                setSelectedIndex,
                setSelectedItem,
                fullWidth,
                fullHeight,
                isMachineScope,
                isMachinesScope: isMachineScope,
                chartTitle,
                ...getChartPropsByScope({
                  statisticsScope,
                  id,
                  ncFileId,
                  allSelected,
                  getMachineStatsPerPeriod,
                  getNcFileStatsMonth,
                  getNcFileStatsDay,
                  getNcFileStatsHour,
                }),
                getMachineEvent,
                changeDate,
                toGo,
                selectedChartDate: selectedChartDate,
                selectedIndex,
                selectedItem,
                isActive,
                sendLog,
                setSelectedChartDate,
                setSelectedChartDateFromCalendar,
                isDay,
                selectedTimeCounter,
                showLine,
                resetMobileChartCount,
                }}
              />
            </InlineErrorBoundary>
            {isWeb && hasNextWindow && (
              <Arrow {...{ C, rotate: 180, onPress: () => changeDate('add'), testID: TEST_IDS.activeProductiveChartNavigation.rightArrow }} />
            )}
          </View>
          <View style={{ width: '100%', paddingLeft: scale(isWeb ? 64 : 14), paddingTop: scale(6), minHeight: scale(20) }}>
            <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>
              {i18n.t('Updated today at')} {localTime().format('HH:00')}.{' '}
              {i18n.t('Swipe to change {{period}}', { period: i18n.t(capitalizeFirstLetter(selectedTime)) || '' })}
            </Text>
          </View>
        </View>
        {!!ncFiles.length && isDay && (
          <FeatureFlags name="ncfile-update-nc-file-type-search-list">
            <InlineErrorBoundary screenName="EventChartsNcFileTypeListBlock">
            <View>
              <View
                style={[
                  {
                    marginTop: scale(20),
                    width: '100%',
                    alignItems: 'center',
                  },
                ]}
              >
                <View style={[getBoxShadowStyle({ type: 'Field', C }), { width: fullWidth, borderRadius: scale(SCREEN_BORDER_RADIUS) }]}>
                  {updateType.map((i, index) => (
                    <Line
                      key={i.name}
                      {...{
                        item: i,
                        index,
                        array: updateType,
                        C,
                        smallSize: true,
                        topBorder: false,
                        roundTop: true,
                        roundBottom: true,
                        textColor: 'Link/Light/Primary',
                        takePhoneLayout: true,
                      }}
                    />
                  ))}
                </View>
              </View>
              <View style={{ width: '100%', paddingHorizontal: scale(isWeb ? 64 : 14), paddingTop: scale(3) }}>
                <Text style={[font.Footnote, { color: C('Label/Light/Tertiary') }]}>
                  {i18n.t('In the selected period {{amount}} NC Files with unknown NC File Type were detected.', { amount: ncFiles.length })}
                </Text>
              </View>
            </View>
            </InlineErrorBoundary>
          </FeatureFlags>
        )}
        {isMachineScope && !isNowSubScreen && (
          <View style={{ width: '100%', alignItems: 'center' }}>
            <View style={{ width: fullWidth }}>
              <View>
                {isPhone && !IS_NARROW_WEB && allSelected && (
                  <View style={{ alignItems: 'center', width: '100%', paddingHorizontal: scale(isPhone ? SCREEN_PADDING_HORIZONTAL : 0) }}>
                    <View style={{ flexDirection: 'row', marginTop: scale(20) }}>
                      {Object.values(mobileGlobalTabs).map((item) => (
                        <View key={item}>
                          <Selector
                            {...{
                              scale,
                              font,
                              selected: selectedMobileGlobalTab === item,
                              variant: 'L',
                              text: i18n.t(item),
                              onPress: () => setSelectedMobileGlobalTab(item),
                            }}
                          />
                        </View>
                      ))}
                    </View>
                  </View>
                )}
                {selectedMobileGlobalTab === mobileGlobalTabs.perMachine && isPhone && allSelected && (
                  <InlineErrorBoundary screenName="EventChartsMachinesListBlock">
                    <MobileMachinesList {...{ ...setSelectedMachine, time, generateImageURL }} />
                  </InlineErrorBoundary>
                )}
                {(selectedMobileGlobalTab === mobileGlobalTabs.perNCFile || !isPhone || (isPhone && !allSelected)) &&
                  !!ncFileStatistics?.data?.length && (
                    <InlineErrorBoundary screenName="EventChartsNcFileItemBlock">
                      <NcFileItem
                        {...{
                          ncFileStatistics,
                          C,
                          generateImageURL,
                          selectedChartDate,
                          allSelected,
                          setSelectedNCFile,
                          machineId: selectedItemFromMachines.id,
                        }}
                      />
                    </InlineErrorBoundary>
                  )}
              </View>
            </View>
          </View>
        )}
        {(!isMachineScope || isNowSubScreen) && (
          <View style={{ width: '100%', alignItems: 'center' }}>
            <View style={{ width: fullWidth }}>
              {!!machineStatisticsForNCFile?.data?.length && (
                <InlineErrorBoundary screenName="EventChartsNcFileDetailsBlock">
                  <NCFileItemForDetailsScreen {...{ machineStatisticsForNCFile, C, generateImageURL, selectedChartDate, allSelected }} />
                </InlineErrorBoundary>
              )}
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

const EventChartsWrapper: FC<EventChartWrapperProps> = (props) => {
  const { timePeriod: selectedTime } = useHandleParams();
  const { safeIsMachinesScreenCheck } = useNavigationCheck();
  const abortControllerNCFile = useRef();

  const {
    getContentElement,
    chartData: data,
    setChartData: setData,
    selectedTimeCounter,
    setSelectedChartDate,
    setSelectedChartDateEnd,
    selectedChartDate,
  } = props;

  const changeDate = (action: string) => {
    let currentDate = localTime();

    if (selectedChartDate) {
      currentDate = localTime(selectedChartDate);
    }

    const { unit: stepUnit, count: stepCount } = getPeriodStep(selectedTime);
    if (action === 'add') {
      currentDate.add(stepCount, stepUnit);
    } else {
      currentDate.subtract(stepCount, stepUnit);
    }

    if (currentDate.clone().startOf(stepUnit).isAfter(localTime())) {
      return;
    }

    const startDate = currentDate.clone().startOf(stepUnit).format();
    const endDate = currentDate.clone().add(stepCount, stepUnit).startOf(stepUnit).format();
    setData({});
    setSelectedChartDate(startDate);
    setSelectedChartDateEnd(endDate);
  };

  return (
    <View
      style={[
        {
          alignItems: 'center',
          width: '100%',
          flex: 1,
        },
        !isPhone || (safeIsMachinesScreenCheck && { paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL) }),
      ]}
    >
      <View
        style={{
          flexDirection: 'row',
          marginBottom: scale(isPhone ? SCREEN_MARGIN_BOTTOM_PHONE : SCREEN_MARGIN_BOTTOM_WEB),
          paddingTop: scale(SCREEN_PADDING_VERTICAL_FOR_ITEM),
          flex: 1,
        }}
      >
        {getContentElement({
          ...props,
          changeDate,
          setSelectedChartDate,
          selectedChartDate,
          chartData: data,
          setChartData: setData,
          selectedTimeCounter,
          abortControllerNCFile,
        })}
      </View>
    </View>
  );
};

export default connect(
  ({
    machines: { machines, selectedItem: selectedItemFromMachines, machineStatistics, ncFileStatistics, machineStatisticsForNCFile },
    time,
    user: { featureFlags },
    machinesV2: { selectedTimeCounter, chartData, selectedChartDate, selectedChartDateEnd, initialEventDate, selectedItem, selectedGroup },
  }) => ({
    machines,
    selectedItemFromMachines,
    machineStatistics,
    ncFileStatistics,
    time,
    featureFlags,
    chartData,
    selectedTimeCounter,
    selectedChartDate,
    initialEventDate,
    selectedItem,
    machineStatisticsForNCFile,
    selectedGroup,
    selectedChartDateEnd,
  }),
  {
    getMachineStatsPerPeriod,
    getMachineEvent,
    sendLog,
    getMachineStatistics,
    getNCFileStatistics,
    generateImageURL,
    setSelectedMachine,
    setSelectedNCFile,
    getMachineEventTimeline,
    machineEventDetailsWallboard,
    getNCFileById,
    getNCFileByName,
    setSelectedNCFileManufacture,
    ncFilesSearch,
    setChartData,
    setSelectedChartDate,
    setSelectedChartDateEnd,
    setInitialEventDate,
    setTimePeriod,
    getMachineStatisticsForNCFile,
    getNcFileStatsMonth,
    getNcFileStatsDay,
    getNcFileStatsHour,
  }
)(
  ColorWrapper(
    React.memo(
      EventChartsWrapper,
      (prev, next) =>
        prev.C.scheme === next.C.scheme &&
        prev.machines === next.machines &&
        prev.ncFileStatistics?.page === next.ncFileStatistics?.page &&
        prev.machineStatistics?.loaded === next.machineStatistics?.loaded &&
        prev.selectedItemFromMachines?.id === next.selectedItemFromMachines.id &&
        prev.machineStatisticsForNCFile === next.machineStatisticsForNCFile &&
        prev.lg === next.lg &&
        prev.selectedTimeCounter === next.selectedTimeCounter &&
        prev.chartData === next.chartData &&
        prev.setChartData === next.setChartData &&
        prev.selectedChartDate === next.selectedChartDate &&
        prev.selectedChartDateEnd === next.selectedChartDateEnd &&
        prev.initialEventDate === next.initialEventDate &&
        prev.setInitialEventDate === next.setInitialEventDate &&
        prev.selectedGroup?.machineGroupId === next.selectedGroup?.machineGroupId
    )
  )
);
